import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

export default class extends Controller {
  static targets = ["categoryLink", "providerLink", "providerBar", "searchBadge"]
  static values = {
    category: String,
    frame: String,
    providerCode: String,
    query: String,
    rootUrl: String,
    sportsbookBannerUrl: String,
    tab: String
  }

  connect() {
    this.sync()
  }

  selectCategory(event) {
    event.preventDefault()

    const link = event.currentTarget
    this.tabValue = this.valueOrBlank(link.dataset.catalogNavTab)
    this.categoryValue = this.valueOrBlank(link.dataset.catalogNavCategory)

    if (["live", "sports"].includes(this.categoryValue)) {
      this.providerCodeValue = ""
    }

    this.sync()
    this.showLoading()
    this.visit()
  }

  selectProvider(event) {
    event.preventDefault()

    const link = event.currentTarget
    this.providerCodeValue = this.valueOrBlank(link.dataset.catalogNavProviderCode)

    this.sync()
    this.showLoading()
    this.visit()
  }

  sync() {
    this.updateCategoryLinks()
    this.updateProviderLinks()
    this.updateProviderBar()
    this.updateSearchBadge()
  }

  updateCategoryLinks() {
    this.categoryLinkTargets.forEach((link) => {
      const tab = this.valueOrBlank(link.dataset.catalogNavTab)
      const category = this.valueOrBlank(link.dataset.catalogNavCategory)

      link.href = this.buildUrl({
        tab,
        category,
        providerCode: ["live", "sports"].includes(category) ? "" : this.providerCodeValue
      })

      this.setActive(link, tab === this.tabValue && category === this.categoryValue)
    })
  }

  updateProviderLinks() {
    this.providerLinkTargets.forEach((link) => {
      const providerCode = this.valueOrBlank(link.dataset.catalogNavProviderCode)
      link.href = this.buildUrl({
        tab: this.tabValue,
        category: this.categoryValue,
        providerCode
      })

      this.setActive(link, providerCode === this.providerCodeValue)
    })
  }

  updateProviderBar() {
    if (!this.hasProviderBarTarget) return

    this.providerBarTarget.hidden = ["live", "sports"].includes(this.categoryValue)
  }

  updateSearchBadge() {
    if (!this.hasSearchBadgeTarget) return

    const hasFilters = this.queryValue !== "" || this.providerCodeValue !== ""
    this.searchBadgeTarget.classList.toggle("d-none", !hasFilters)
  }

  visit() {
    Turbo.visit(this.buildUrl({
      tab: this.tabValue,
      category: this.categoryValue,
      providerCode: this.providerCodeValue
    }), { frame: this.frameValue })
  }

  showLoading() {
    const frame = document.getElementById(this.frameValue)
    if (!frame) return

    const sports = this.categoryValue === "sports"
    const message = sports ? "試合情報を取得しています。" : "ゲームを読み込んでいます。"
    const sportsBanner = sports && this.hasSportsbookBannerUrlValue ? `
      <div class="catalog-sportsbook-loading-banner">
        <img src="${this.sportsbookBannerUrlValue}" alt="World Cup 26" width="1983" height="793" loading="eager" fetchpriority="high">
      </div>
    ` : ""
    const contentClass = sports ? "catalog-sportsbook-loading" : ""
    frame.innerHTML = `
      <div class="container mb-4">
        <div class="${contentClass}">
          ${sportsBanner}
          <div class="catalog-loading-card" role="status" aria-live="polite">
            <div class="spinner-border text-primary" aria-hidden="true"></div>
            <div class="catalog-loading-title">読み込み中</div>
            <div class="catalog-loading-sub">${message}</div>
          </div>
        </div>
      </div>
    `
  }

  buildUrl({ tab, category, providerCode }) {
    const url = new URL(this.rootUrlValue, window.location.origin)

    if (tab !== "") {
      url.searchParams.set("tab", tab)
    }

    if (category !== "") {
      url.searchParams.set("category", category)
    }

    if (this.queryValue !== "") {
      url.searchParams.set("query", this.queryValue)
    }

    if (!["live", "sports"].includes(category) && providerCode !== "") {
      url.searchParams.set("provider_code", providerCode)
    }

    return `${url.pathname}${url.search}`
  }

  setActive(link, active) {
    link.classList.toggle("active", active)

    if (active) {
      link.setAttribute("aria-current", "page")
    } else {
      link.removeAttribute("aria-current")
    }
  }

  valueOrBlank(value) {
    return value || ""
  }
}
